// @flow

import _ from 'lodash';

import { getJsonWithAccountId } from '@zapier/toolbox-browser-fetch';
import {
  buildEntityActions,
  buildEntityApi,
  buildEntitySelectors,
} from '@zapier/toolbox-redux-framework';

import { Account } from '@zapier/shared-entities';
import * as UserNotification from 'app/entities/UserNotification';
import { createId, emptyObject } from 'app/common/CommonUtils';
import partialWithDefault from './utils/partialWithDefault';

const POLL_INTERVAL = 3 * 1000;
const LAST_POLL_RESULTS_TIMESTAMP = 'lastPollResultsTimestamp';
const PENDING = 'pending';
const SUCCESS = 'success';
const DELETED = 'deleted';

export const type = 'massActionFeedbackChannel';

export const api = buildEntityApi(type, '/api/v3/stream/channel');

api.load = (id, getState) =>
  getJsonWithAccountId(
    Account.selectors.all.currentAccountId(getState()),
    api.modelEndpoint(id, getState)
  );

/*
   The data structures we get back from the backend API look like this:

   unreplayableErrors: {
     status: ['taskid1', 'taskid2']
   }

   statuses: {
     taskid1: 'status',
     taskid2: 'status'
   }
*/
export const selectors = buildEntitySelectors(type, sel => {
  // This assumes just one (or zero) active channels, which is the case for now.
  // TODO: will need to query by type 'taskReplay' or somesuch eventually.
  sel.all.taskReplayChannel = state => sel.all.entities(state)[0];

  sel.successfulTaskIds = entity =>
    Object.keys(_.pickBy(sel.statuses(entity), s => s === SUCCESS));
  sel.all.taskReplayChannelSuccessfulTaskIds = _.flow(
    sel.all.taskReplayChannel,
    sel.successfulTaskIds
  );

  sel.isRunning = entity => _.some(sel.statuses(entity), s => s === PENDING);
  sel.lastPollResultsTimestamp = _.partial(
    sel.stateItem,
    LAST_POLL_RESULTS_TIMESTAMP
  );
  sel.statusForTaskId = (taskId, entity) =>
    sel.dataItem('statuses', entity)[taskId];
  sel.statuses = partialWithDefault(sel.dataItem, ['statuses'], emptyObject);
  sel.unreplayableErrors = _.partial(sel.dataItem, 'unreplayableErrors');
  sel.globalFailures = partialWithDefault(sel.dataItem, ['globalFailures'], []);
  sel.hasGlobalFailures = entity =>
    (sel.globalFailures(entity) || []).length > 0;
  sel.globalFailureType = _.partial(sel.dataItem, 'globalFailureType');
  sel.isMassActionsDisabled = _.partial(sel.dataItem, 'isMassActionsDisabled');
  sel.isDeleted = (taskId, entity) =>
    sel.statusForTaskId(taskId, entity) === DELETED;
  sel.action = _.partial(sel.dataItem, 'action');
  sel.statusCounts = entity => _.countBy(sel.statuses(entity));
  sel.statusCountsSortedBySuccess = entity => {
    const { success, deleted, ...rest } = sel.statusCounts(entity);
    const successObj = success ? { success } : {};
    const deletedObj = deleted ? { deleted } : {};
    return {
      ...successObj,
      ...deletedObj,
      ...rest,
    };
  };

  // counts by status for unreplayableErrors and statuses
  sel.allStatusCounts = entity => ({
    ...sel.statusCountsSortedBySuccess(entity),
    ...sel.unreplayableErrors(entity),
  });
  sel.totalCount = entity => _.sum(_.values(sel.allStatusCounts(entity)));
  sel.playedCount = entity =>
    sel.totalCount(entity) - (sel.statusCounts(entity)[PENDING] || 0);
  sel.countByStatus = (status, entity) => sel.statusCounts(entity)[status] || 0;
  sel.successCount = _.partial(sel.countByStatus, SUCCESS);
  sel.deletedCount = _.partial(sel.countByStatus, DELETED);
  sel.completedCount = entity =>
    sel.successCount(entity) + sel.deletedCount(entity);
  sel.action = _.partial(sel.dataItem, 'action');
});

export const actions = buildEntityActions(
  type,
  selectors,
  api,
  entityActions => {
    entityActions.recordTimestamp = _.partial(
      entityActions.updateEntityStateItem,
      LAST_POLL_RESULTS_TIMESTAMP
    );

    entityActions.createFeedbackChannel = (fields, onFinish: ?Function) => {
      const id = fields.channelId || createId() + '';

      return (dispatch, getState) => {
        // only one feedback channel notification at a time for now
        dispatch(entityActions.resetCollection());
        dispatch(UserNotification.actions.resetCollection());

        dispatch(
          actions.createCollectionUiEntity({
            ...fields,
            id,
            unreplayableErrors: fields.unreplayableErrors || {},
            statuses: {},
          })
        );

        const poll = () => {
          dispatch(actions.loadEntity(null, fields.channelId)).then(
            ({ timestampId }) => {
              dispatch(actions.recordTimestamp(timestampId, fields.channelId));
              dispatch(UserNotification.actions.loadCollection());

              const feedbackEntity = selectors.all.entity(
                fields.channelId,
                getState()
              );
              if (selectors.isRunning(feedbackEntity)) {
                setTimeout(poll, POLL_INTERVAL);
              } else if (onFinish) {
                onFinish(feedbackEntity);
              }
            }
          );
        };
        if (fields.channelId) {
          poll();
        } else {
          dispatch(UserNotification.actions.loadCollection());
        }
      };
    };
  }
);
